import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import AppHeader from "../Comp/AppHeader";
import FooterNew from "../Comp/FooterNew";

const ZXFileManagerPage = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const googlePlayUrl =
    "https://play.google.com/store/apps/details?id=com.filemanager.fileexplorer.junkcleaner";
  const mobileImgUrl = "/images/ZXmockup.png";
  const zxLogoUrl = "/images/zx.png";
  const playstoreImgUrl =
    "https://rareprob.com/images/homepage/google.webp";

  const features = [
    {
      icon: "📁",
      title: "Smart File Explorer & Organizer",
      description:
        "Effortlessly organize, search, copy, move, rename, batch delete, and archive your internal storage and SD card files with lightning speed.",
    },
    {
      icon: "🔐",
      title: "Callock Vault & Secret Hider",
      description:
        "Protect sensitive photos, secret videos, and personal files behind passcode security. Your private data stays encrypted locally.",
    },
    {
      icon: "🧹",
      title: "Junk File Cleaner & Optimizer",
      description:
        "Scan and clear unnecessary residual cache, temp files, obsolete APKs, and duplicate media to recover storage space instantly.",
    },
    {
      icon: "⬇️",
      title: "Social Media Video Downloader",
      description:
        "Save videos, reels, stories, and photos from your favorite public social media platforms directly into your phone storage.",
    },
    {
      icon: "📄",
      title: "PDF Converter & Scanner Tools",
      description:
        "Scan paper documents to PDF, convert images to PDF, compress large document sizes, and merge multiple PDFs with ease.",
    },
    {
      icon: "☁️",
      title: "Google Drive Cloud Integration",
      description:
        "Securely connect your Google Drive to browse, upload, download, and manage cloud files directly inside ZX File Manager.",
    },
    {
      icon: "⚡",
      title: "Wireless PC & Device Sharing",
      description:
        "Transfer files wirelessly between Android devices and PC via FTP/Wi-Fi hotspot at ultra-high speed with zero mobile data usage.",
    },
    {
      icon: "🎬",
      title: "Built-in HD Media Player & Browser",
      description:
        "Preview photos, stream 4K/HD videos, play audio files, and browse web links seamlessly without relying on third-party apps.",
    },
  ];

  const faqs = [
    {
      question: "What is the main purpose of ZX File Manager?",
      answer:
        "ZX File Manager is designed as a complete Android storage management application. Its primary purpose is to empower users to browse, organize, compress, clean, and back up local phone files as well as manage their connected Google Drive files inside a single intuitive interface.",
    },
    {
      question: "Why does ZX File Manager request Google Drive access?",
      answer:
        "ZX File Manager requests Google Drive permissions strictly to allow users to connect their Google Drive account, view cloud files/folders, and upload or download files between local Android storage and their cloud account.",
    },
    {
      question: "Does ZX File Manager store or share my Google account data?",
      answer:
        "No. Authorization is conducted directly between you and Google via secure OAuth 2.0. ZX File Manager does not collect, store, or share your Google account password, personal details, or cloud files on external servers.",
    },
    {
      question: "How does the Callock Vault protect my hidden photos and videos?",
      answer:
        "Callock Vault locks your sensitive media and files behind passcode protection. All vault files are stored securely on your local device storage with zero server uploads, giving you complete privacy.",
    },
    {
      question: "How do I transfer files wirelessly between my phone and PC?",
      answer:
        "With the Wireless PC Transfer feature, ZX File Manager creates a secure local FTP server link over Wi-Fi. Simply enter the provided IP link in your computer's browser or file explorer to manage your phone files without cables or mobile data usage.",
    },
    {
      question: "Is ZX File Manager free to download and use?",
      answer:
        "Yes, ZX File Manager is completely free to download from the Google Play Store with full access to essential file management features.",
    },
  ];

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>ZX File Manager - Android File Explorer &amp; Cloud Storage Manager</title>
        <meta
          name="description"
          content="Official homepage for ZX File Manager. Fast Android file explorer, Callock secret photo vault, storage junk cleaner, and Google Drive cloud manager developed by AppSpace Solutions."
        />
        <meta
          name="keywords"
          content="ZX File Manager, File Explorer Android, Callock Vault, File Hider, Junk Cleaner, Google Drive Sync, PDF Scanner, Wireless PC Transfer, AppSpace Solutions"
        />
        <meta property="og:title" content="ZX File Manager - Android File Explorer &amp; Cloud Manager" />
        <meta
          property="og:description"
          content="Manage local Android files, hide private photos in Callock vault, clean storage junk, and sync Google Drive files securely."
        />
        <meta property="og:image" content={zxLogoUrl} />
        <meta property="og:type" content="website" />
      </Helmet>

      <AppHeader name="" indexSlide={3} />

      <main className="w-full bg-white overflow-hidden text-gray-800">
        {/* HERO SECTION */}
        <section className="w-full bg-gradient-to-b from-[#2e0854] to-[#4c1d95] text-white pt-24 sm:pt-32 lg:pt-36 pb-20 lg:pb-28 px-6 sm:px-10 lg:px-20 relative">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center relative z-10">
            {/* Left Content */}
            <div className="lg:col-span-7 flex flex-col items-start space-y-6">
              <div className="flex items-center space-x-4">
                <img
                  src={zxLogoUrl}
                  alt="ZX File Manager App Logo"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl shadow-lg object-cover"
                />
                <div>
                  {/* Exact App Name matching GCP OAuth Consent Screen */}
                  <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                    ZX File Manager
                  </h1>
                  <p className="text-purple-200 text-sm sm:text-base font-medium mt-1">
                    Android File Explorer &amp; Cloud Storage Manager
                  </p>
                </div>
              </div>

              {/* Explicit Purpose Statement */}
              <div className="space-y-3">
                <p className="text-purple-100 text-base sm:text-lg font-normal leading-relaxed max-w-xl">
                  <strong>ZX File Manager</strong> is an all-in-one Android application designed to help users efficiently manage internal storage, organize files, protect private media with a Callock passcode vault, clean residual junk, and seamlessly integrate with Google Drive for cloud storage access.
                </p>
                <p className="text-purple-200 text-xs sm:text-sm leading-relaxed max-w-xl">
                  Developed by <strong>AppSpace Solutions</strong>. Designed for fast file navigation, local privacy protection, and secure cloud file management.
                </p>
              </div>

              {/* Action Area */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-5 w-full sm:w-auto">
                <a
                  href={googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex justify-center items-center transform hover:scale-105 transition-transform duration-200"
                  id="zx-playstore-btn"
                >
                  <img
                    src={playstoreImgUrl}
                    alt="Get ZX File Manager on Google Play"
                    className="h-14 sm:h-16 w-auto object-contain rounded-xl shadow-lg"
                  />
                </a>

                <Link
                  to="/privacy/zx-file-manager"
                  className="inline-flex justify-center items-center px-5 py-3.5 text-purple-200 hover:text-white font-medium text-sm sm:text-base transition-colors"
                  id="zx-privacy-link"
                >
                  Privacy Policy &rarr;
                </Link>
              </div>
            </div>

            {/* Right Minimalist Phone Graphic */}
            <div className="lg:col-span-5 flex justify-center items-center relative my-4 lg:my-0">
              <div className="relative">
                <img
                  src={mobileImgUrl}
                  alt="ZX File Manager User Interface"
                  className="w-[240px] sm:w-[300px] lg:w-[320px] drop-shadow-2xl transform hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </section>

        {/* GOOGLE DRIVE API & APPLICATION PURPOSE DISCLOSURE SECTION */}
        <section className="py-16 px-6 sm:px-10 lg:px-20 bg-purple-50/60 border-b border-purple-100">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="space-y-2">
              <span className="text-purple-800 font-semibold text-xs tracking-wider uppercase">
                App Purpose &amp; Google API Disclosures
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                How ZX File Manager Uses Google Drive Data
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
              <div className="bg-white p-6 rounded-2xl shadow-sm space-y-3">
                <h3 className="font-bold text-base text-gray-900 flex items-center space-x-2">
                  <span>☁️</span>
                  <span>Purpose of Google Drive Integration</span>
                </h3>
                <p>
                  ZX File Manager offers an integrated cloud file browser allowing users to connect their personal Google Drive account. This functionality enables users to view, upload, download, rename, move, and delete files between their local Android device storage and Google Drive.
                </p>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm space-y-3">
                <h3 className="font-bold text-base text-gray-900 flex items-center space-x-2">
                  <span>🔒</span>
                  <span>Data Protection &amp; Limited Use Compliance</span>
                </h3>
                <p>
                  Your Google Drive files move directly between your device and Google Drive servers via encrypted HTTPS connections. ZX File Manager does not transfer, store, or share your Google user data on any third-party servers.
                </p>
              </div>
            </div>

            {/* MANDATORY GOOGLE LIMITED USE POLICY STATEMENT */}
            <div className="bg-white p-6 rounded-2xl border-l-4 border-purple-700 shadow-sm text-xs sm:text-sm text-gray-600 leading-relaxed space-y-2">
              <p className="font-semibold text-gray-900">
                Google API Services User Data Policy Notice:
              </p>
              <p>
                ZX File Manager's use and transfer to any other app of information received from Google APIs will adhere to the{" "}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-700 font-semibold underline hover:text-purple-900"
                >
                  Google API Services User Data Policy
                </a>
                , including the Limited Use requirements.
              </p>
            </div>
          </div>
        </section>

        {/* MINIMALIST STATS BANNER */}
        <section className="py-12 px-6 sm:px-10 lg:px-20 bg-white">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="p-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-purple-800 mb-1">
                100%
              </h3>
              <p className="text-gray-600 text-sm font-medium">Free &amp; Secure Storage</p>
            </div>
            <div className="p-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-purple-800 mb-1">
                Callock
              </h3>
              <p className="text-gray-600 text-sm font-medium">Secret Photo Vault</p>
            </div>
            <div className="p-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-purple-800 mb-1">
                Junk Cleaner
              </h3>
              <p className="text-gray-600 text-sm font-medium">Storage Optimizer</p>
            </div>
            <div className="p-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-purple-800 mb-1">
                Cloud
              </h3>
              <p className="text-gray-600 text-sm font-medium">Google Drive Integration</p>
            </div>
          </div>
        </section>

        {/* MINIMALIST FEATURES GRID */}
        <section className="py-20 px-6 sm:px-10 lg:px-20 bg-gray-50/50">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-16 space-y-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                Core Functionality &amp; Application Features
              </h2>
              <p className="text-gray-500 text-base sm:text-lg">
                ZX File Manager provides all essential storage utilities for Android devices.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="flex flex-col justify-start space-y-3 p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <div className="text-3xl">{feature.icon}</div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MINIMALIST PRIVACY & CALLOCK SPOTLIGHT */}
        <section className="py-20 px-6 sm:px-10 lg:px-20 bg-white">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                Callock Vault: Keep Your Secrets Private &amp; Secure
              </h2>
              <p className="text-gray-600 text-base leading-relaxed">
                Protect sensitive personal photos, secret videos, financial documents, and private folders from unauthorized access. With the built-in Callock Vault, your items are hidden behind a password-protected lock screen.
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex items-start space-x-3">
                  <span className="text-purple-600 text-lg font-bold">✓</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">Passcode Protection</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Set up custom passwords or PIN locks to secure your private vault.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-purple-600 text-lg font-bold">✓</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">100% Local Storage Privacy</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Your hidden media never leaves your phone and is never uploaded to external servers.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-purple-600 text-lg font-bold">✓</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">Instant Unhide &amp; Restore</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Unhide your media anytime back to original storage locations in one tap.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 bg-purple-900 text-white rounded-2xl p-8 shadow-sm">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">🔐</span>
                  <div>
                    <h3 className="text-xl font-bold">Callock Vault Privacy</h3>
                    <p className="text-xs text-purple-200">Local Encryption Hider</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <p className="text-xl font-bold text-white">Photos &amp; Videos</p>
                    <p className="text-xs text-purple-200 mt-1">Hidden Gallery Media</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">Documents</p>
                    <p className="text-xs text-purple-200 mt-1">Encrypted Folder Hider</p>
                  </div>
                </div>

                <p className="text-xs text-purple-200 leading-relaxed pt-2">
                  ZX File Manager respects your personal privacy. We do not collect or store your private files or vault passcodes on external servers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* MINIMALIST FREQUENTLY ASKED QUESTIONS */}
        <section className="py-20 px-6 sm:px-10 lg:px-20 bg-gray-50/50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12 space-y-2">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-500 text-base">
                Everything you need to know about ZX File Manager features, app purpose, and security.
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {faqs.map((faq, index) => (
                <div key={index} className="py-5">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left font-semibold text-gray-900 text-base sm:text-lg flex justify-between items-center space-x-4 hover:text-purple-700 transition-colors focus:outline-none"
                    aria-expanded={activeFaq === index}
                  >
                    <span>{faq.question}</span>
                    <span className="text-gray-400 font-light text-xl flex-shrink-0">
                      {activeFaq === index ? "−" : "+"}
                    </span>
                  </button>

                  {activeFaq === index && (
                    <div className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed pr-6">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MINIMALIST CALL TO ACTION BANNER */}
        <section className="py-20 px-6 sm:px-10 lg:px-20 bg-gradient-to-b from-[#2e0854] to-[#1e0538] text-white text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Download ZX File Manager for Android
            </h2>
            <p className="text-purple-200 text-base sm:text-lg font-normal">
              Get ZX File Manager today for fast local file browsing, Callock passcode vault security, storage junk cleaning, and Google Drive cloud integration.
            </p>

            <div className="pt-4 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a
                href={googlePlayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="transform hover:scale-105 transition-transform duration-200"
              >
                <img
                  src={playstoreImgUrl}
                  alt="Get ZX File Manager on Google Play"
                  className="h-16 w-auto rounded-xl shadow-lg"
                />
              </a>

              <Link
                to="/privacy/zx-file-manager"
                className="text-purple-200 hover:text-white font-medium text-sm sm:text-base transition-colors"
              >
                Privacy Policy &rarr;
              </Link>
            </div>
          </div>
        </section>
      </main>

      <FooterNew bgColor="#1e0538" />
    </>
  );
};

export default ZXFileManagerPage;



